import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Archive as ArchiveIcon, Lock, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsCard from "../components/NewsCard";
import DailyBriefModal from "../components/DailyBriefModal";
import { getStoredEmail } from "../lib/useNewsezeUser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  ["all", "All sections"],
  ["top", "Front Page"],
  ["government", "Politics"],
  ["money", "Make Money"],
  ["realestate", "Real Estate"],
  ["family", "Family"],
  ["technology", "Tech"],
  ["ai", "AI"],
  ["health", "Health"],
  ["medicine", "Medicine"],
  ["sports", "Sports"],
  ["hollywood", "Hollywood"],
  ["entertainment", "Entertainment"],
  ["culture", "Culture"],
  ["world", "World"],
  ["voices", "Voices"],
  ["editorial", "Editorial"],
];

export default function Archives() {
  const email = getStoredEmail();
  const [category, setCategory] = useState("all");
  const [date, setDate] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBrief, setShowBrief] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 30 };
      if (category !== "all") params.category = category;
      if (date) params.date = date;
      if (email) params.email = email;
      const r = await axios.get(`${API}/archives`, { params, timeout: 12000 });
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category, date, page, email]); // eslint-disable-line
  useEffect(() => {
    axios.get(`${API}/archives/dates`, { params: { days: 60 } }).then((r) => setDates(r.data || [])).catch(() => {});
  }, []);

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;
  const isSub = data?.is_subscriber;

  return (
    <>
      <Header onOpenBrief={() => setShowBrief(true)} />
      <main className="max-w-[1280px] mx-auto px-5 py-8">
        <div className="section-title">
          <span className="pip" />
          <h2 data-testid="archives-title">Newseze Archives</h2>
          <span className="ml-auto text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--muted)]">
            Every story we publish — permanent record · {total.toLocaleString()} articles
          </span>
        </div>
        <p className="text-[13.5px] text-[var(--muted)] mt-1 max-w-[820px]" data-testid="archives-tagline">
          The Newseze archive is a permanent, trust-ranked record of every story we publish — like a digital
          newspaper morgue. Subscribers get full access to summaries and tags; everyone can browse headlines.
        </p>

        {!email && (
          <div className="rounded-xl border border-[var(--gold)] bg-[#fff7d6] p-4 my-5 flex items-center justify-between flex-wrap gap-3" data-testid="archives-paywall">
            <div>
              <div className="font-black text-[14px] flex items-center gap-2"><Lock size={14} /> Subscribers unlock summaries + tags</div>
              <p className="text-[12.5px] text-[var(--muted)] mt-1">Headlines are free. Subscribe to read summaries and pin favorite stories.</p>
            </div>
            <button onClick={() => setShowBrief(true)} className="btn gold whitespace-nowrap" data-testid="archives-subscribe">
              Subscribe free
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 my-5 bg-white p-4 rounded-xl border border-[var(--line)]" data-testid="archives-filters">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-[var(--blue)]" />
            <select value={date} onChange={(e) => { setDate(e.target.value); setPage(1); }} className="field text-[13px]" data-testid="archives-date">
              <option value="">All dates</option>
              {dates.map(({ date: d, count }) => (
                <option key={d} value={d}>{d} ({count})</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <ArchiveIcon size={14} className="text-[var(--blue)]" />
            <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} className="field text-[13px]" data-testid="archives-category">
              {CATEGORIES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          <span className="ml-auto text-[11px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">
            Page {page} of {pages}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[var(--blue)]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center text-[var(--muted)] italic py-12" data-testid="archives-empty">No articles found for this filter.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => isSub ? (
              <NewsCard key={a.id} article={a} onBrief={() => {}} onShare={() => {}} />
            ) : (
              <div key={a.id} className="rounded-xl border border-[var(--line)] bg-white overflow-hidden hover:border-[var(--blue)] transition" data-testid={`archive-stub-${a.id}`}>
                {a.image && <Link to={`/article/${a.id}`}><img src={a.image} alt={a.title} className="w-full h-40 object-cover" /></Link>}
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--blue)]">{a.category}</div>
                  <Link to={`/article/${a.id}`} className="text-[15px] font-black tracking-[-0.02em] text-[var(--ink)] block mt-1 hover:text-[var(--blue)]">
                    {a.title}
                  </Link>
                  <div className="text-[11px] text-[var(--muted)] mt-2">{a.source} · {new Date(a.published).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8" data-testid="archives-pagination">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn icon" data-testid="archives-prev">
              <ChevronLeft size={14} /> Prev
            </button>
            <span className="text-[12px] font-bold tabular-nums">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="btn icon" data-testid="archives-next">
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </main>
      <Footer />
      {showBrief && <DailyBriefModal onClose={() => setShowBrief(false)} />}
    </>
  );
}
